import React, { useEffect, useState, useRef } from "react";
import { SendPostRequest } from "../../http";
import { useSelector } from "react-redux";
import "./PuzzleSlider.css";

const PuzzleSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [homeBanners, setHomeBanners] = useState([]);
  const [showPuzzle, setShowPuzzle] = useState(true);
  const [showFullImage, setShowFullImage] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [titleVisible, setTitleVisible] = useState(false);
  const [descriptionVisible, setDescriptionVisible] = useState(false);
  const sitedata = useSelector((store) => store.global.loginData);
  const containerRef = useRef(null);
  const autoSlideTimerRef = useRef(null);

  // Animation timing constants
  const PUZZLE_ANIMATION_DURATION = 2500; // Slower puzzle animation
  const IMAGE_TRANSITION_DURATION = 800; // Smooth image transition
  const CONTENT_BOX_DELAY = 300; // Content box appears after image
  const TITLE_ANIMATION_DELAY = 500; // Title animation after content box
  const DESCRIPTION_ANIMATION_DELAY = 1000; // Description after 1 second of title
  const CONTENT_DISPLAY_DURATION = 5000; // 5 seconds to display content

  useEffect(() => {
    const brandingData = async () => {
      const siteTheme = sitedata?.options?.theme;
      const response = await SendPostRequest("branding/banners", { theme: siteTheme });
      setHomeBanners(response?.data || []);

      if (response?.data?.length > 0) {
        // Run puzzle only once at start
        setShowPuzzle(true);
        setTimeout(() => {
          setShowFullImage(true);
          setShowPuzzle(false);
          setTimeout(() => {
            setContentVisible(true);
            setTimeout(() => {
              setTitleVisible(true);
              setTimeout(() => {
                setDescriptionVisible(true);
              }, DESCRIPTION_ANIMATION_DELAY);
            }, TITLE_ANIMATION_DELAY);
          }, CONTENT_BOX_DELAY);
        }, PUZZLE_ANIMATION_DURATION);
      }
    };
    brandingData();
  }, [sitedata]);


  const [prevSlideImage, setPrevSlideImage] = useState(null);

  const changeSlide = (nextIndex) => {
    if (homeBanners.length === 0) return;

    // Prevent same slide re-animating
    if (nextIndex === currentSlide) return;

    // Store current slide image as background
    setPrevSlideImage(homeBanners[currentSlide]?.banner_image);

    // Reset all animation states
    setShowPuzzle(true);
    setShowFullImage(false);
    setContentVisible(false);
    setTitleVisible(false);
    setDescriptionVisible(false);

    // Change slide
    setCurrentSlide(nextIndex);

    // Start animation sequence
    setTimeout(() => {
      setShowPuzzle(true);

      // After puzzle, show full image and hide puzzle
      setTimeout(() => {
        setShowFullImage(true);
        setShowPuzzle(false);

        // Show content after image
        setTimeout(() => {
          setContentVisible(true);

          setTimeout(() => {
            setTitleVisible(true);

            setTimeout(() => {
              setDescriptionVisible(true);
            }, DESCRIPTION_ANIMATION_DELAY);
          }, TITLE_ANIMATION_DELAY);
        }, CONTENT_BOX_DELAY);
      }, PUZZLE_ANIMATION_DURATION);
    }, 100);
  };


  const nextSlide = () => {
    const nextIndex = (currentSlide + 1) % homeBanners.length;
    changeSlide(nextIndex);
  };

  const prevSlide = () => {
    const nextIndex = (currentSlide - 1 + homeBanners.length) % homeBanners.length;
    changeSlide(nextIndex);
  };

  const goToSlide = (i) => changeSlide(i);

  // Auto loop with proper timing
  useEffect(() => {
    if (homeBanners.length === 0) return;

    // Clear any existing timer
    if (autoSlideTimerRef.current) {
      clearInterval(autoSlideTimerRef.current);
    }

    // Calculate total animation duration for auto-slide
    const totalAnimationTime = PUZZLE_ANIMATION_DURATION +
      IMAGE_TRANSITION_DURATION +
      CONTENT_BOX_DELAY +
      TITLE_ANIMATION_DELAY +
      DESCRIPTION_ANIMATION_DELAY +
      CONTENT_DISPLAY_DURATION;

    // Set up new timer with proper interval
    autoSlideTimerRef.current = setInterval(() => {
      nextSlide();
    }, totalAnimationTime);

    return () => {
      if (autoSlideTimerRef.current) {
        clearInterval(autoSlideTimerRef.current);
      }
    };
  }, [homeBanners.length, currentSlide]);

  if (homeBanners.length === 0) return null;

  const createPuzzlePieces = () => {
    const pieces = [];
    const rows = 5;
    const cols = 5;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const delay = (row * 0.2 + col * 0.1).toFixed(2); // 👈 staggered assemble
        const pieceStyle = {
          backgroundImage: `url(${homeBanners[currentSlide]?.banner_image})`,
          backgroundSize: `${cols * 100}% ${rows * 100}%`,
          backgroundPosition: `${(col / (cols - 1)) * 100}% ${(row / (rows - 1)) * 100}%`,
          animationDelay: `${delay}s`,
          animationDuration: "3s",
        };

        pieces.push(
          <div
            key={`${row}-${col}`}
            className="new-puzzle-banner-slider-piece"
            style={pieceStyle}
          />
        );
      }
    }
    return pieces;
  };


  return (
    <div
      className="new-puzzle-banner-slider-container"
      ref={containerRef}
      style={{
        backgroundImage: prevSlideImage ? `url(${prevSlideImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="new-puzzle-banner-slider-slide">
        {/* Puzzle Pieces - Show actual image pieces */}
        {showPuzzle && (
          <div className="new-puzzle-banner-slider-puzzle-container">
            {createPuzzlePieces()}
          </div>
        )}

        {/* Full Image - Smoothly appears after puzzle pieces form complete image */}
       {showFullImage && (
  <div
    className={`new-puzzle-banner-slider-full-image ${
      showFullImage ? "show-full-image" : ""
    }`}
    style={{ backgroundImage: `url(${homeBanners[currentSlide]?.banner_image})` }}
  />
)}


        {/* Content */}
        {homeBanners[currentSlide]?.banner_category === "custom-text" && (
  <div className="container new-puzzle-banner-slider-content-container">
    <div
      className={`new-puzzle-banner-slider-content ${
        contentVisible ? 'new-puzzle-banner-slider-content-visible' : ''
      } ${
        homeBanners[currentSlide]?.banner_alignment === "right"
          ? "align-left"
          : "align-center"
      }`}
    >
      <h1 className={`new-puzzle-banner-slider-title banner-title-font ${titleVisible ? 'new-puzzle-banner-slider-title-animate' : ''}`}>
        {homeBanners[currentSlide]?.banner_title}
      </h1>
      <p className={`new-puzzle-banner-slider-subtitle ${descriptionVisible ? 'new-puzzle-banner-slider-subtitle-animate' : ''}`}>
        {homeBanners[currentSlide]?.banner_description}
      </p>
    </div>
  </div>
)}

      </div>

      {/* Arrows - Instant slide change on click */}
      <button className="new-puzzle-banner-slider-arrow new-puzzle-banner-slider-prev" onClick={prevSlide}>‹</button>
      <button className="new-puzzle-banner-slider-arrow new-puzzle-banner-slider-next" onClick={nextSlide}>›</button>

      {/* Dots */}
      {/* <div className="new-puzzle-banner-slider-dots">
        {homeBanners.map((_, i) => (
          <button
            key={i}
            className={`new-puzzle-banner-slider-dot ${i === currentSlide ? "new-puzzle-banner-slider-active" : ""}`}
            onClick={() => goToSlide(i)}
          />
        ))}
      </div> */}
    </div>
  );
};

export default PuzzleSlider;